import { useEffect } from 'react';
import { ComponentConfig } from '@measured/puck';
import { useFormContext } from 'react-hook-form';
import { PhoneNumberControl } from '../../../page-builder/components/form-fields/PhoneNumber';
import { FormData } from '../../../page-builder/interface/checkoutInterface';
import {
  marginFields,
  MarginProps,
} from '../../../page-builder/puck/reusable-props/margin';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../services/constants';
import CustomTooltip from '../../../components/common/tooltip';
import { IAddressOptions } from '../../interface/formInterface';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
  getPhoneCode,
} from '../../utils/helper';
import { AddressFormField } from '../../puck/reusable-props/addressFormOptions';
import {
  paddingFields,
  paddingProps,
  PaddingProps,
} from '../../puck/reusable-props/padding';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import {
  fieldHeightProps,
  inputFieldHeightProps,
} from '../../puck/reusable-props/inputfield-height';
import { defaultBorderProps } from '../../utils/constants';
import { marginProps } from '../../puck/reusable-props/NewMargin';
import { outlineProps } from '../../puck/reusable-props/Outline';

export type PhoneNumberFieldProps = {
  option: { Phone: IAddressOptions };
  border: IBorderValueProps;
} & MarginProps &
  fieldHeightProps &
  PaddingProps;

export const PhoneNumberField = ({
  useAppSelector,
}: ReduxDispatchProps &
  ReduxSelectorProps): ComponentConfig<PhoneNumberFieldProps> => {
  return {
    label: (
      <CustomTooltip title="PhoneNumber">
        <span>Phone Number</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        label: 'Border',
        render: ({ value, onChange }) => (
          <Border
            value={value}
            onChange={value => onChange({ ...value })}
            title="Border"
          />
        ),
      },
      option: {
        type: 'custom',
        label: 'Options',
        render: ({ value, onChange }) => {
          return (
            <div className="shippingFields">
              {Object?.keys(value || {})?.map(key => {
                return (
                  <AddressFormField
                    formName="Phone Number"
                    key={key}
                    name={key}
                    value={value?.[key as keyof { Phone: IAddressOptions }]}
                    onChange={newValue =>
                      onChange({ ...value, [key]: newValue })
                    }
                    index={100}
                  />
                );
              })}
            </div>
          );
        },
      },
      ...inputFieldHeightProps(true),
    },
    defaultProps: {
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      ...borderProps,
      option: {
        Phone: {
          header: {
            hide: false,
            text: 'Phone Number',
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Phone',
          requiredValidation: true,
          validationText: 'Phone number is required',
          ...defaultBorderProps,
          color: '#a1a1a1',
          fontSize: '14',
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
      },
      heightField: {
        height: {
          desktop: '42',
          tablet: '42',
          mobile: '42',
        },
        deviceType: 'desktop',
      },
    },
    render: ({ margin, option, padding, editMode, border, heightField }) => {
      const { setValue } = useFormContext<FormData>();

      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getMarginStyles(margin, currentDeviceType);
      const paddingStyles = getPaddingStyles(padding, currentDeviceType);

      useEffect(() => {
        setValue('phone_code', getPhoneCode() || '+1');
      }, [setValue]);
      return (
        <div
          className={`${border?.borderStyle?.[currentDeviceType]}`}
          style={{
            ...marginStyles,
            ...paddingStyles,
            ...getBorderProps(border, currentDeviceType),
          }}>
          <div>
            <PhoneNumberControl
              selectProps={{
                id: 'phone_code',
                autoComplete: 'phone_code',
              }}
              inputProps={{
                id: 'PhoneNumber',
              }}
              fieldHeight={heightField?.height?.[currentDeviceType]}
              placeholder={option?.Phone?.placeholder}
              phoneNumberName="CustomerData.PhoneNumber"
              header={option?.Phone?.header}
              radiusBorder={option?.Phone?.border}
              outline={option?.Phone?.outline}
              placeHolderColor={option?.Phone?.color}
              placeHolderFontSize={option?.Phone?.fontSize}
              backgroundColor={option?.Phone?.textBackgroundColor}
              textColor={option?.Phone?.textColor}
              placeHolderweight={option?.Phone?.fontWeight}
              PlaceHolderTextAlign={option?.Phone?.textAlign}
              placeHolderFontFamily={option?.Phone?.fontFamily}
              placeHolderLetterSpace={option?.Phone?.letterSpace}
              placeholderlineHeight={option?.Phone?.lineHeight}
              phoneCodeName="phone_code"
              rules={{
                required: option?.Phone?.requiredValidation
                  ? option?.Phone?.validationText || 'Phone number is required'
                  : false,
              }}
            />
          </div>
        </div>
      );
    },
  };
};
